import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { ChevronDown, ChevronsLeft, ChevronsRight, ExternalLink } from 'lucide-react';
import type { NavGroup, NavItem } from '@/lib/nav/types';
import { isNavActive } from '@/lib/nav/staff';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';
import { Tooltip } from '@/components/ui/Tooltip';
import { Logo, LogoMark } from './Logo';

interface SidebarProps {
  groups: readonly NavGroup[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const itemBase = 'group relative flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors duration-150';

function Section({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { pathname } = useLocation();
  const active = isNavActive(pathname, item.to, item.end);
  const [manual, setManual] = useState<boolean | null>(null);
  const open = !collapsed && (manual ?? active);
  const Icon = item.icon;

  const row = (
    <NavLink
      to={item.to}
      end={item.end || Boolean(item.children)}
      aria-label={collapsed ? item.label : undefined}
      aria-current={active ? 'page' : undefined}
      className={cn(itemBase, collapsed && 'justify-center px-0', active ? 'text-white' : 'text-navy-200 hover:bg-white/[0.06] hover:text-white')}
    >
      {active && (
        <>
          <span className="absolute inset-0 -z-0 rounded-xl bg-white/[0.12]" aria-hidden="true" />
          <span className="absolute -left-3 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-sapphire-300" aria-hidden="true" />
        </>
      )}
      <Icon className={cn('relative h-[1.125rem] w-[1.125rem] shrink-0 transition-colors', active ? 'text-sapphire-200' : 'text-navy-300 group-hover:text-white')} aria-hidden="true" />
      {!collapsed && <span className="relative flex-1 truncate">{item.label}</span>}
    </NavLink>
  );

  return (
    <li>
      <div className="relative flex items-center">
        {collapsed ? (
          <Tooltip content={item.label} placement="bottom">
            {row}
          </Tooltip>
        ) : (
          row
        )}
        {item.children && !collapsed && (
          <button
            type="button"
            aria-label={`${open ? 'Collapse' : 'Expand'} ${item.label}`}
            aria-expanded={open}
            onClick={() => setManual(!open)}
            className="absolute right-1.5 grid h-7 w-7 place-items-center rounded-lg text-navy-300 transition hover:bg-white/10 hover:text-white"
          >
            <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')} aria-hidden="true" />
          </button>
        )}
      </div>
      {item.children && open && (
        <ul className="ml-[1.4rem] mt-1 space-y-0.5 border-l border-white/10 pl-3">
          {item.children.map((child) => {
            const childActive = isNavActive(pathname, child.to, child.end);
            const exactBest = item.children?.every((c) => c === child || !isNavActive(pathname, c.to, c.end) || c.to.length <= child.to.length);
            const current = childActive && exactBest;
            return (
              <li key={child.id}>
                <NavLink
                  to={child.to}
                  end={child.end}
                  aria-current={current ? 'page' : undefined}
                  className={cn('block truncate rounded-lg px-2.5 py-1.5 text-[0.8125rem] transition-colors', current ? 'bg-white/10 font-medium text-white' : 'text-navy-300 hover:bg-white/[0.06] hover:text-white')}
                >
                  {child.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

/** Deep-navy navigation rail. Permission filtering happens before groups reach this component. */
export function Sidebar({ groups, collapsed, onToggleCollapsed }: SidebarProps) {
  const { t } = useI18n();
  return (
    <aside
      className={cn(
        'no-print relative hidden shrink-0 flex-col overflow-hidden bg-midnight text-white transition-[width] duration-300 ease-out lg:flex motion-reduce:transition-none',
        'sticky top-0 h-dvh border-r border-white/5',
        collapsed ? 'w-[4.75rem]' : 'w-[17rem]',
      )}
    >
      <div className={cn('relative flex h-16 shrink-0 items-center border-b border-white/[0.07]', collapsed ? 'justify-center px-0' : 'px-5')}>
        <NavLink to="/staff" aria-label="Lumina staff dashboard" className="rounded-lg">
          {collapsed ? <LogoMark size={34} /> : <Logo size={34} tone="onDark" subtitle="Staff" />}
        </NavLink>
      </div>
      <nav aria-label={t('nav.main')} className="scroll-thin relative flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
        {groups.map((group, gi) => (
          <div key={group.id} className={cn(gi > 0 && 'mt-5')}>
            {collapsed ? gi > 0 && <div className="mx-3 mb-3 h-px bg-white/10" /> : <p className="mb-1.5 px-3 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-navy-400">{group.label}</p>}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <Section key={item.id} item={item} collapsed={collapsed} />
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className={cn('relative shrink-0 space-y-1 border-t border-white/[0.07] p-3')}>
        <NavLink to="/" className={cn(itemBase, 'text-navy-300 hover:bg-white/[0.06] hover:text-white', collapsed && 'justify-center px-0')} aria-label={collapsed ? t('nav.publicCatalogue') : undefined}>
          <ExternalLink className="h-[1.125rem] w-[1.125rem] shrink-0" aria-hidden="true" />
          {!collapsed && <span className="truncate">{t('nav.publicCatalogue')}</span>}
        </NavLink>
        <button type="button" onClick={onToggleCollapsed} aria-label={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')} aria-pressed={collapsed} className={cn(itemBase, 'text-navy-300 hover:bg-white/[0.06] hover:text-white', collapsed && 'justify-center px-0')}>
          {collapsed ? <ChevronsRight className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" /> : <ChevronsLeft className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />}
          {!collapsed && <span>{t('shell.collapseSidebar')}</span>}
        </button>
      </div>
    </aside>
  );
}
